import { useRouter } from 'next/router';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';

import useMediaQuery from 'hooks/useMediaQuery';
import useScrollPosition from 'hooks/useScrollPosition';

import LINKS from 'constants/links';

/**
 * This is the same for both desktop and mobile.
 */
const TOPBAR_MAIN_HEIGHT = 60;
const TOPBAR_MOBILE_SEARCH_HEIGHT = 58;
const TOPBAR_AD_HEIGHT = {
  DESKTOP: 40,
  MOBILE: 64,
};
const TOPBAR_BOTTOM_HEIGHT = {
  DESKTOP: 58,
  MOBILE: 73,
};
const PATHNAMES_WITH_DISABLED_DESKTOP_TOPBAR_BOTTOM = [
  LINKS.BLOG,
  LINKS.NEW_COUPON,
  LINKS.NEW_OFFER,
  LINKS.NEW_THREAD,
];
const PATHNAMES_WITH_ACTIVE_TOPBAR_OFFERS_TAB_NAV = [
  LINKS.HOME,
  LINKS.LOWEST_PRICE,
  LINKS.HIGH_OFFERS,
];

const isDesktopTopbarBottomActive = (pathname) =>
  PATHNAMES_WITH_DISABLED_DESKTOP_TOPBAR_BOTTOM.every(
    (link) => !pathname.includes(link.substring(0, link.length - 1))
  );

const isMobileTopbarBottomActive = (pathname) =>
  PATHNAMES_WITH_ACTIVE_TOPBAR_OFFERS_TAB_NAV.includes(pathname);

const getDesktopTopbarHeight = ({
  isTopbarBottomActive,
  isTopbarAdActive,
  scrollYPosition = 0,
}) =>
  TOPBAR_MAIN_HEIGHT +
  (isTopbarAdActive && scrollYPosition <= TOPBAR_AD_HEIGHT.DESKTOP
    ? TOPBAR_AD_HEIGHT.DESKTOP - scrollYPosition
    : null) +
  (isTopbarBottomActive ? TOPBAR_BOTTOM_HEIGHT.DESKTOP : null);

const getMobileTopbarHeight = ({
  isTopbarAdActive = false,
  isTopbarBottomActive,
  scrollYPosition = 0,
}) =>
  TOPBAR_MAIN_HEIGHT +
  (isTopbarAdActive && scrollYPosition <= TOPBAR_AD_HEIGHT.MOBILE
    ? TOPBAR_AD_HEIGHT.MOBILE - scrollYPosition
    : null) +
  (isTopbarBottomActive ? TOPBAR_BOTTOM_HEIGHT.MOBILE : null);

const TopbarContext = createContext({
  categories: [],
  featuredLanding: null,
  isTopbarActive: true,
  highlightedLandings: [],
  searchQuery: null,
  stores: [],
  topbarAd: null,
  topbarDesktop: null,
  topbarMobile: null,
  setTopbarAd: () => {},
  setMobileTopbarSearchActive: () => {},
});

TopbarContext.displayName = 'TopbarContext';

const TopbarProvider = ({
  categories = [],
  children,
  featuredLanding = null,
  highlightedLandings = [],
  isTopbarActive = true,
  stores = [],
  topbarAd = null,
}) => {
  const router = useRouter();
  const { isLg } = useMediaQuery();
  const [topbarDesktop, setTopbarDesktop] = useState({
    constants: {
      adHeight: TOPBAR_AD_HEIGHT.DESKTOP,
      topbarBottomHeight: TOPBAR_BOTTOM_HEIGHT.DESKTOP,
      topbarMainHeight: TOPBAR_MAIN_HEIGHT,
    },
    isTopbarBottomActive: isDesktopTopbarBottomActive(router.asPath),
    isTopbarActive,
    height: getDesktopTopbarHeight({
      isTopbarBottomActive: isDesktopTopbarBottomActive(router.asPath),
      isTopbarAdActive: !!topbarAd,
    }),
    topbarAdHeight: TOPBAR_AD_HEIGHT.DESKTOP,
  });
  const [topbarMobile, setTopbarMobile] = useState({
    constants: {
      adHeight: TOPBAR_AD_HEIGHT.MOBILE,
      topbarBottomHeight: TOPBAR_BOTTOM_HEIGHT.MOBILE,
      topbarMainHeight: TOPBAR_MAIN_HEIGHT,
      topbarSearchHeight: TOPBAR_MOBILE_SEARCH_HEIGHT,
    },
    isSearchActive: false,
    isTopbarActive,
    isTopbarBottomActive: isMobileTopbarBottomActive(router.asPath),
    height: getMobileTopbarHeight({
      isTopbarAdActive: !!topbarAd,
      isTopbarBottomActive: isMobileTopbarBottomActive(router.asPath),
    }),
    topbarAdHeight: TOPBAR_AD_HEIGHT.MOBILE,
  });
  const [topbarAdMeta, setTopbarAdMeta] = useState(topbarAd);

  /**
   * Hides `TopbarBottom` on scroll and shows on smaller scrolls to top.
   * Also updates `height` prop accordingly
   */
  useScrollPosition(
    ({ currPos, prevPos }) => {
      const isScrollingToTop = prevPos.y >= currPos.y;

      if (isLg && currPos.y <= TOPBAR_AD_HEIGHT.DESKTOP) {
        setTopbarDesktop((prevState) => ({
          ...prevState,
          height: getDesktopTopbarHeight({
            isTopbarAdActive: !!topbarAdMeta,
            isTopbarBottomActive: prevState.isTopbarBottomActive,
            scrollYPosition: currPos.y,
          }),
        }));
      }

      if (!isLg && currPos.y <= TOPBAR_AD_HEIGHT.MOBILE) {
        setTopbarMobile((prevState) => ({
          ...prevState,
          height: getMobileTopbarHeight({
            isTopbarAdActive: !!topbarAdMeta,
            isTopbarBottomActive: prevState.isTopbarBottomActive,
            scrollYPosition: currPos.y,
          }),
        }));
      }

      /**
       * This is used as if the topbar bottom appears using less than
       * its own height scroll diff (last - current scroll position). It will
       * cause a loop when it appears, the diff between last and
       * current will be as if the user were scrolling down endlessly.
       */
      if (
        currPos.y - prevPos.y > TOPBAR_MAIN_HEIGHT ||
        prevPos.y - currPos.y > TOPBAR_MAIN_HEIGHT
      ) {
        if (isLg) {
          setTopbarDesktop((prevState) => {
            const isTopbarBottomActive = isDesktopTopbarBottomActive(
              router.asPath
            )
              ? isScrollingToTop
              : prevState.isTopbarBottomActive;

            return {
              ...prevState,
              isTopbarBottomActive,
              height: getDesktopTopbarHeight({
                isTopbarBottomActive,
                isTopbarAdActive: !!topbarAd,
                scrollYPosition: currPos.y,
              }),
            };
          });

          return;
        }

        setTopbarMobile((prevState) => {
          const isTopbarBottomActive = isMobileTopbarBottomActive(router.asPath)
            ? isScrollingToTop
            : prevState.isTopbarBottomActive;

          return {
            ...prevState,
            isTopbarBottomActive,
            height: getMobileTopbarHeight({
              isTopbarAdActive: !!topbarAdMeta,
              isTopbarBottomActive,
              scrollYPosition: currPos.y,
            }),
          };
        });
      }
    },
    { deps: [router.asPath, isLg] }
  );

  /*
   * Try to refetch topbar ad if it didn't come as a prop from the server,
   * like in static pages. It causes a CLS issue.
   */
  useEffect(() => {
    const refetchTopbarAd = async () => {
      if (!topbarAdMeta) {
        const { getTopbarAd } = await import('lib/ads');
        const fetchedTopbarAd = await getTopbarAd();

        if (fetchedTopbarAd) {
          setTopbarAdMeta(fetchedTopbarAd);
        }
      }
    };

    refetchTopbarAd();
  }, []);

  /**
   * If route changes, updates `isTopbarBottomActive` accordingly. Some routes
   * should't have an active `TopbarBottom`. Also closes mobile `TopbarSearch`
   */
  useEffect(() => {
    const closeMobileTopbarSearch = () =>
      setTopbarMobile((prevState) => ({ ...prevState, isSearchActive: false }));

    router.events.on('routeChangeComplete', closeMobileTopbarSearch);

    if (isLg) {
      setTopbarDesktop((prevState) => ({
        ...prevState,
        isTopbarBottomActive: isDesktopTopbarBottomActive(router.asPath),
        height: getDesktopTopbarHeight({
          isTopbarBottomActive: isDesktopTopbarBottomActive(router.asPath),
          isTopbarAdActive: !!topbarAd,
          scrollYPosition: prevState.scrollYPosition,
        }),
      }));
    } else {
      setTopbarMobile((prevState) => ({
        ...prevState,
        isTopbarBottomActive: isMobileTopbarBottomActive(router.asPath),
        height: getMobileTopbarHeight({
          isTopbarAdActive: !!topbarAdMeta,
          isTopbarBottomActive: isMobileTopbarBottomActive(router.asPath),
          scrollYPosition: prevState.scrollYPosition,
        }),
      }));
    }

    return () => {
      router.events.off('routeChangeComplete', closeMobileTopbarSearch);
    };
  }, [isLg, router.asPath]);

  const setTopbarAd = useCallback((ad) => setTopbarAdMeta(ad), []);

  const setTopbarMobileSearchActive = useCallback(
    (isSearchActive) =>
      setTopbarMobile((prevState) => ({
        ...prevState,
        isSearchActive: isSearchActive,
        height: getMobileTopbarHeight({
          isTopbarAdActive: !!topbarAdMeta,
          isTopbarBottomActive: prevState.isTopbarBottomActive,
          scrollYPosition: prevState.scrollYPosition,
        }),
      })),
    []
  );

  return (
    <TopbarContext.Provider
      value={{
        categories,
        featuredLanding,
        highlightedLandings,
        isTopbarActive,
        stores,
        topbarAd: topbarAdMeta,
        topbarDesktop,
        topbarMobile,
        setTopbarAd,
        setTopbarMobileSearchActive,
      }}
    >
      {children}
    </TopbarContext.Provider>
  );
};

const useTopbar = () => useContext(TopbarContext);

export { useTopbar };

export default TopbarProvider;
